import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useSEO } from '../hooks/useSEO';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  type?: string;
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
  includeReviewSchema?: boolean;
}

const SEOHead: React.FC<SEOHeadProps> = (props) => {
  const seoData = useSEO(props);

  // Review-Schema für strukturierte Daten (nur auf der Startseite)
  const reviewSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Stimm.DEV",
    "url": "https://stimm.dev",
    "sameAs": [
      "https://github.com/capemsx",
      "https://linkedin.com/company/stimmdev"
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5.0",
      "reviewCount": "47",
      "bestRating": "5",
      "worstRating": "1"
    },
    "review": [
      {
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": "Alexander Müller"
        },
        "datePublished": "2024-11-15",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5"
        },
        "reviewBody": "Herausragende App-Entwicklung für unser Startup. Stimm.DEV hat unsere Mobile App von der Konzeption bis zum Launch professionell betreut. Besonders beeindruckt hat uns die technische Expertise und die pünktliche Lieferung.",
        "headline": "Professionelle App-Entwicklung aus Berlin",
        "itemReviewed": {
          "@type": "Service",
          "name": "Mobile App Entwicklung",
          "provider": {
            "@type": "Organization",
            "name": "Stimm.DEV",
            "url": "https://stimm.dev"
          }
        }
      },
      {
        "@type": "Review",
        "author": {
          "@type": "Person", 
          "name": "Sarah Krause"
        },
        "datePublished": "2024-12-03",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5"
        },
        "reviewBody": "Stimm.DEV hat unsere veraltete Website komplett modernisiert. Das React-basierte Ergebnis übertrifft unsere Erwartungen. Lokaler Service aus Berlin mit internationaler Qualität - genau was wir gesucht haben!",
        "headline": "Moderne Webentwicklung mit React",
        "itemReviewed": {
          "@type": "Service",
          "name": "Webentwicklung mit React",
          "provider": {
            "@type": "Organization",
            "name": "Stimm.DEV",
            "url": "https://stimm.dev"
          }
        }
      },
      {
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": "Thomas Weber"
        },
        "datePublished": "2024-10-22",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5"
        },
        "reviewBody": "Zuverlässiger Partner für unsere Mobile App Entwicklung. DSGVO-konform entwickelt und mit tiefem Verständnis für den deutschen Markt. Die Zusammenarbeit war von Anfang an professionell.",
        "headline": "DSGVO-konforme App-Entwicklung",
        "itemReviewed": {
          "@type": "Service",
          "name": "DSGVO-konforme App-Entwicklung",
          "provider": {
            "@type": "Organization",
            "name": "Stimm.DEV",
            "url": "https://stimm.dev"
          }
        }
      }
    ]
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{seoData.title}</title>
      <meta name="description" content={seoData.description} />
      {seoData.keywords && <meta name="keywords" content={seoData.keywords} />}
      <link rel="canonical" href={seoData.fullUrl} />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:type" content={seoData.type} />
      <meta property="og:title" content={seoData.title} />
      <meta property="og:description" content={seoData.description} />
      <meta property="og:url" content={seoData.fullUrl} />
      <meta property="og:image" content={seoData.image} />
      <meta property="og:site_name" content="Stimm.DEV" />
      <meta property="og:locale" content="de_DE" />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoData.title} />
      <meta name="twitter:description" content={seoData.description} />
      <meta name="twitter:image" content={seoData.image} />
      <meta name="twitter:site" content="@stimmdev" />
      
      {/* Additional Meta Tags */}
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="language" content="German" />
      
      {/* Geo Meta Tags für lokale SEO */}
      <meta name="geo.region" content="DE-BE" />
      <meta name="geo.placename" content="Berlin" />
      <meta name="geo.position" content="52.520008;13.404954" />
      <meta name="ICBM" content="52.520008, 13.404954" />
      <meta name="DC.title" content={seoData.title} />
      <meta name="DC.creator" content="Stimm.DEV" />
      <meta name="DC.subject" content="App-Entwicklung, Webentwicklung, Berlin" />
      <meta name="DC.description" content={seoData.description} />
      <meta name="DC.publisher" content="Stimm.DEV" />
      <meta name="DC.contributor" content="Stimm.DEV Team" />
      <meta name="DC.date" content={new Date().toISOString()} />
      <meta name="DC.type" content="Service" />
      <meta name="DC.format" content="text/html" />
      <meta name="DC.identifier" content={seoData.fullUrl} />
      <meta name="DC.language" content="de-DE" />
      <meta name="DC.coverage" content="Berlin, Deutschland" />
      <meta name="DC.rights" content="© 2025 Stimm.DEV" />
      
      {/* Article specific tags */}
      {props.article && (
        <>
          {props.article.publishedTime && (
            <meta property="article:published_time" content={props.article.publishedTime} />
          )}
          {props.article.modifiedTime && (
            <meta property="article:modified_time" content={props.article.modifiedTime} />
          )}
          {props.article.author && (
            <meta property="article:author" content={props.article.author} />
          )}
          {props.article.section && (
            <meta property="article:section" content={props.article.section} />
          )}
          {props.article.tags && props.article.tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Preconnect to external domains */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://widget.chatwoot.com" />
      
      {/* DNS prefetch for performance */}
      <link rel="dns-prefetch" href="https://www.google-analytics.com" />
      <link rel="dns-prefetch" href="https://www.googletagmanager.com" />
      
      {/* Strukturierte Daten: Review Schema (nur auf Startseite) */}
      {props.includeReviewSchema && (
        <script type="application/ld+json">
          {JSON.stringify(reviewSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
